var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useConfigStore from '../stores/configStore';
import useWidgetStore, { CurrentTabEnum } from '../stores/widgetStore';
import useSaasPlatformStore from '../stores/saasPlatformStore';
import useCustomerStore from '../stores/customerStore';
import useProductStore from '../stores/productStore';
import { UNIFIED_ACTION } from '../utils/types';
import getPolicyInfo from '../utils/getPolicyInfo';
import { deepMerge } from '../utils/common';
import shade from '../utils/shade';
import api from '../api';
var FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
export var formatMoney = function (amount) {
    var value = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(value))
        return 'N/A';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};
export var formatDisplayMoney = function (amount) {
    return formatMoney(amount).replace('.00', '');
};
export var getCodeAndLabel = function (quoteKey) {
    var parts = "".concat(quoteKey || '').toLowerCase().split('-');
    var type = parts.length > 1 ? parts[1] : parts[0];
    if (!type) {
        return {
            underwriter: '',
            code: quoteKey,
            quoteAcronym: quoteKey.length > 3
                ? quoteKey.substring(0, 2).toUpperCase()
                : quoteKey.toUpperCase(),
            label: quoteKey,
            description: "Add ".concat(quoteKey.toUpperCase(), " to your protection plan."),
            quoteTitle: "Get your ".concat(quoteKey, " quote"),
            quoteDescription: 'Complete a few more questions to see your rate.'
        };
    }
    var underwriter = parts.length > 1 ? parts[0].toUpperCase() : '';
    switch (type.toLowerCase()) {
        case 'wc':
            return {
                underwriter: underwriter,
                code: 'WC',
                quoteAcronym: 'WC',
                label: "Workers' Compensation",
                description: "Add Workers' Compensation to your protection plan.",
                quoteTitle: "Get your Workers' Compensation quote",
                quoteDescription: "Workers' Comp requires a few additional details specific to your workforce. Answer a short set of questions and we'll generate your quote right away.",
                informationRequired: [
                    'Employee roles & job classifications',
                    'Payroll figures by job type',
                    'State(s) where employees work',
                ]
            };
        case 'bop':
            return {
                underwriter: underwriter,
                code: 'BOP',
                quoteAcronym: 'BOP',
                label: 'Business Owners Policy (General Liability + Property)',
                description: "Add Business Owners Policy to your protection plan.",
                quoteTitle: "Get your Business Owner's Policy quote",
                quoteDescription: "A BoP bundles property and liability coverage for your business. Answer a short set of questions and we'll generate your quote right away.",
                informationRequired: [
                    'Premises, equipment & inventory details',
                    'Liability coverage preferences',
                    'Prior claims & loss history',
                ]
            };
        case 'gl':
            return {
                underwriter: underwriter,
                code: 'GL',
                quoteAcronym: 'GL',
                label: 'General Liability',
                description: "Add General Liability to your protection plan.",
                quoteTitle: "Get your General Liability quote",
                quoteDescription: 'Complete a few more questions to see your rate.'
            };
        case 'pl':
        case 'eo':
            return {
                underwriter: underwriter,
                code: 'PL',
                quoteAcronym: 'PL',
                label: 'Professional Liability',
                description: "Add Professional Liability to your protection plan.",
                quoteTitle: "Get your Professional Liability quote",
                quoteDescription: 'Complete a few more questions to see your rate.'
            };
        case 'cyber':
            return {
                underwriter: underwriter,
                code: 'Cyber',
                quoteAcronym: 'CY',
                label: 'Cyber Liability',
                description: "Add Cyber Liability to your protection plan.",
                quoteTitle: "Get your Cyber Liability quote",
                quoteDescription: 'Complete a few more questions to see your rate.'
            };
        default:
            var code = type.toUpperCase();
            return {
                underwriter: underwriter,
                code: code,
                quoteAcronym: code.length > 3 ? code.substring(0, 2) : code,
                label: quoteKey,
                description: "Add ".concat(code.toUpperCase(), " to your protection plan."),
                quoteTitle: "Get your ".concat(quoteKey, " quote"),
                quoteDescription: 'Complete a few more questions to see your rate.'
            };
    }
};
export var POLICY_DESCRIPTIONS = {
    PL: 'Protects you from being sued for negligently performing your services, even if you haven’t made a mistake. Coverage also includes defense costs associated with a lawsuit.',
    GL: 'Protects your business from another person or business’s claims of bodily injury, associated medical costs, and damage to their property.',
    CY: 'Protects against computer-related crimes and losses. These can include targeted attacks, such as malware and phishing, or a misplaced laptop containing confidential material.'
};
export var STATUS_MAPPER = {
    referred: 'REFERRED',
    rejected: 'REJECTED',
    eliminated: 'ELIMINATED',
    failed: 'FAILED',
    quoted: 'READY',
    queued: 'LOADING',
    outdated: 'EXPIRED'
};
var BASE_FREQUENCIES = [
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
    { key: 'semi-yearly', label: 'Semi-Annual' },
    { key: 'yearly', label: 'Annual' },
];
export var useMultiQuoteLogic = function (_a) {
    var _b, _c, _d, _e, _f;
    var quotes = _a.quotes, parentQuoteId = _a.parentQuoteId, parentQuote = _a.parentQuote, bindingQuoteKeys = _a.bindingQuoteKeys;
    var style = useConfigStore().style;
    var queryClient = useQueryClient();
    var _g = useWidgetStore(), unifiedState = _g.unifiedState, setSelectedPolicyPayload = _g.setSelectedPolicyPayload, setUnifiedState = _g.setUnifiedState, setCurrentTab = _g.setCurrentTab, setFormActive = _g.setFormActive, setAnswers = _g.setAnswers, setSelectedQuote = _g.setSelectedQuote, setActiveDraftQuote = _g.setActiveDraftQuote, setFooterOverride = _g.setFooterOverride, setApplicationDataId = _g.setApplicationDataId, setCurrQuestionGroup = _g.setCurrQuestionGroup;
    var _h = useProductStore(), setProduct = _h.setProduct, productBranding = _h.productBranding;
    var Products = useSaasPlatformStore().Products;
    var applicationFormData = useCustomerStore().applicationFormData;
    var ackQuote = useMutation(api.quote.ackQuote, {
        onSuccess: function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, queryClient.invalidateQueries(['customer'])];
                    case 1:
                        _a.sent();
                        setGlobalAckLoading(false);
                        return [2 /*return*/];
                }
            });
        }); }
    });
    var submitForManualReview = useMutation(api.quote.submitForManualReview, {
        onSuccess: function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, queryClient.invalidateQueries(['customer'])];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); }
    });
    var _j = useState([]), acknowledgedQuotes = _j[0], setAcknowledgedQuotes = _j[1];
    var _k = useState({}), selections = _k[0], setSelections = _k[1];
    var _l = useState(false), quoteAckLoading = _l[0], setQuoteAckLoading = _l[1];
    var _m = useState(false), globalAckLoading = _m[0], setGlobalAckLoading = _m[1];
    var _o = useState(0), tick = _o[0], setTick = _o[1];
    var loadingStartTimesRef = useRef({});
    var primaryColor = (style === null || style === void 0 ? void 0 : style.primaryColor) || '#3B82F6';
    var disabledBuyNowBgColor = shade(primaryColor, 0.9);
    var getProduct = function (quoteKey) {
        var _a;
        var appFormDataKeys = Object.keys(applicationFormData || {});
        var externalId = getCodeAndLabel(quoteKey).code;
        var productId = '';
        var application = {};
        if (appFormDataKeys.includes(externalId)) {
            application = applicationFormData[externalId];
            productId = application['productId'];
        }
        else if (appFormDataKeys.length === 1) {
            application = applicationFormData[appFormDataKeys[0]];
            productId =
                application["".concat(externalId, "-productId")] || application['productId'];
        }
        var product = (_a = Products === null || Products === void 0 ? void 0 : Products.find) === null || _a === void 0 ? void 0 : _a.call(Products, function (p) { return p.id == productId; });
        return { product: product, application: application };
    };
    var handleFrequencySelect = function (quoteKey, externalQuoteReference, quoteId, plan, frequency, quoteLabel) {
        setSelections(function (prev) {
            var _a;
            var _b;
            if (((_b = prev[quoteKey]) === null || _b === void 0 ? void 0 : _b.frequency) === frequency) {
                return prev;
            }
            return __assign(__assign({}, prev), (_a = {}, _a[quoteKey] = {
                quoteKey: quoteKey,
                frequency: frequency,
                selectedPlan: plan,
                quoteLabel: quoteLabel,
                externalQuoteReference: externalQuoteReference,
                quoteId: quoteId
            }, _a));
        });
    };
    var isAcknowledge = function (quote) {
        return (!quote.hideLimitsAndActions &&
            !quote.isPolicyActive &&
            !quote.isBinding &&
            !quote.hasError &&
            quote.isAckQuestionsAvailable &&
            !quote.isAcknowledged);
    };
    var handleAck = function (quote) {
        var selection = selections[quote.quoteKey];
        if (!selection)
            return;
        if (quote.externalId.includes('hiscox')) {
            setFooterOverride(true);
        }
        setUnifiedState({
            action: UNIFIED_ACTION.SHOW_ACK_QUESTIONS_TO_FE,
            activeQuoteId: quote.kaynaQuoteId,
            externalId: quote.externalId,
            parentQuoteId: parentQuoteId,
            activeQuote: {
                quote: quote,
                state: 'Ack-inProgress',
                selectedPlan: selection
            }
        });
    };
    var handleBuyNow = function (quote) {
        var _a, _b;
        if (isAcknowledge(quote)) {
            handleAck(quote);
        }
        else {
            var selection = selections[quote.quoteKey];
            if (!selection)
                return;
            if (quote.externalId.includes('hiscox')) {
                setFooterOverride(true);
            }
            setUnifiedState({
                activeQuoteId: quote.kaynaQuoteId,
                collectPayment: (_a = quote === null || quote === void 0 ? void 0 : quote.externalQuote) === null || _a === void 0 ? void 0 : _a.collectPayment,
                paymentMethods: (_b = quote === null || quote === void 0 ? void 0 : quote.externalQuote) === null || _b === void 0 ? void 0 : _b.paymentMethods,
                parentQuoteId: parentQuoteId,
                activeQuote: { quote: quote, state: 'Buy-inProgress' }
            });
            setSelectedPolicyPayload({ selectedPlan: [selection] });
        }
    };
    var handleUpdateLimit = function (kaynaQuoteId, externalId, quoteId) {
        if (externalId.includes('hiscox')) {
            setFooterOverride(true);
        }
        setUnifiedState({
            action: UNIFIED_ACTION.SHOW_COVERAGE_QUESTIONS_TO_FE,
            activeQuoteId: kaynaQuoteId,
            externalId: externalId,
            parentQuoteId: parentQuoteId,
            quoteId: quoteId
        });
    };
    var handleApplicationStart = function (quoteKey, restart) {
        var _a;
        var key = quoteKey;
        var type = quoteKey.split('-')[1];
        if (type === 'pl') {
            type = 'eo';
            key = "".concat(quoteKey.split('-')[0], "-").concat(type);
        }
        var kaynaQuote = quotes[key] || parentQuote;
        var _b = getProduct(key), product = _b.product, application = _b.application;
        var answers = deepMerge(application.applicationDetails || {}, ((_a = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.items) === null || _a === void 0 ? void 0 : _a.requiredParams) || {});
        answers.your_business.RequestedTypesOfInsurance = [type];
        setApplicationDataId(parentQuote.externalId);
        setAnswers(answers);
        setProduct(product);
        setFormActive(true);
        setCurrentTab(CurrentTabEnum.POLICY);
        setSelectedQuote(null);
        setActiveDraftQuote(null);
        setUnifiedState({
            parentQuoteId: parentQuoteId,
            productType: key,
            productBranding: productBranding,
            isRestart: restart
        });
    };
    var viewPolicyDetails = function (kaynaQuoteId, externalId) {
        if (externalId.includes('hiscox')) {
            setFooterOverride(true);
        }
        setUnifiedState({ activeQuoteId: kaynaQuoteId });
        setCurrentTab(CurrentTabEnum.POLICY_INFO);
    };
    // Precompute Base Keys
    var _p = useMemo(function () {
        var _a, _b, _c;
        var PRODUCT_ORDER = ['wc', 'eo', 'gl', 'cyber', 'bop'];
        var keys = typeof quotes === 'object' && quotes !== null && !Array.isArray(quotes)
            ? Object.keys(quotes).sort(function (a, b) {
                var _a, _b;
                var aType = (_a = a.split('-')[1]) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                var bType = (_b = b.split('-')[1]) === null || _b === void 0 ? void 0 : _b.toLowerCase();
                return PRODUCT_ORDER.indexOf(aType) - PRODUCT_ORDER.indexOf(bType);
            })
            : [];
        var all = [];
        var allowedProducts = (_c = (_b = (_a = parentQuote === null || parentQuote === void 0 ? void 0 : parentQuote.Product) === null || _a === void 0 ? void 0 : _a.ProductType) === null || _b === void 0 ? void 0 : _b.partnerData) === null || _c === void 0 ? void 0 : _c.products;
        if (allowedProducts === null || allowedProducts === void 0 ? void 0 : allowedProducts.length) {
            all = allowedProducts;
        }
        else {
            var currentQuotes_1 = new Set();
            keys.forEach(function (q) {
                var p = q.includes('-')
                    ? q.split('-')[1].toUpperCase()
                    : q.toUpperCase();
                currentQuotes_1.add(p === 'EO' ? 'PL' : p === 'CYBER' ? 'Cyber' : p);
            });
            all = Array.from(currentQuotes_1);
        }
        return { quoteKeys: keys, allQuotes: all };
    }, [quotes, parentQuote]), quoteKeys = _p.quoteKeys, allQuotes = _p.allQuotes;
    // Precompute Fully Processed UI Data
    var _q = useMemo(function () {
        var groupQuotes = {};
        var startedCodes = new Set();
        var activeOrBindingCodes = new Set();
        quoteKeys.forEach(function (quoteKey) {
            var policyType = getCodeAndLabel(quoteKey);
            startedCodes.add(policyType.code);
            var kaynaQuote = quotes[quoteKey];
            var policy = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.policy;
            var isPolicyActive = !!policy &&
                (policy.insuranceStatus === 'active' ||
                    policy.insuranceStatus === 'bound' ||
                    policy.insuranceStatus === 'pending');
            if (isPolicyActive) {
                activeOrBindingCodes.add(policyType.code);
            }
            if (!groupQuotes[policyType.code])
                groupQuotes[policyType.code] = [];
            groupQuotes[policyType.code].push(quoteKey);
        });
        // Determine if GL and BOP are active/binding to enforce mutual exclusivity in the UI
        var hasActiveGL = activeOrBindingCodes.has('GL');
        var hasActiveBOP = activeOrBindingCodes.has('BOP');
        var processedNotStarted = allQuotes
            .filter(function (code) { return !startedCodes.has(code); })
            .map(function (code) {
            // Disable GL if BOP is active, and vice versa
            var isDisabledByConflict = (code === 'GL' && hasActiveBOP) || (code === 'BOP' && hasActiveGL);
            return __assign(__assign({ startKey: "pending-".concat(code.toLowerCase()) }, getCodeAndLabel(code)), { isDisabledByConflict: isDisabledByConflict, message: isDisabledByConflict
                    ? 'General Liability and BOP cannot be purchased together'
                    : '' });
        });
        var processedGroups = Object.entries(groupQuotes).map(function (_a) {
            var groupCode = _a[0], keys = _a[1];
            var groupInfo = getCodeAndLabel(keys[0]);
            var mappedQuotes = keys.map(function (quoteKey) {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
                var policyType = getCodeAndLabel(quoteKey);
                var kaynaQuote = quotes[quoteKey];
                var quote = (_b = (_a = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _a === void 0 ? void 0 : _a.quotes) === null || _b === void 0 ? void 0 : _b[quoteKey];
                var policy = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.policy;
                var partnerRejected = (kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.status) === 'rejected';
                var rawError = (_c = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _c === void 0 ? void 0 : _c.error;
                var partnerErrorMsg = rawError && typeof rawError === 'object'
                    ? rawError.message || rawError.code || JSON.stringify(rawError)
                    : rawError || (partnerRejected ? 'Unknown Error' : undefined);
                var quoteStatusRaw = (_d = quote === null || quote === void 0 ? void 0 : quote.status) === null || _d === void 0 ? void 0 : _d.toLowerCase();
                var quoteMappedStatus = STATUS_MAPPER[partnerRejected ? kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.status : quoteStatusRaw];
                var isReferred = quoteMappedStatus === 'REFERRED';
                var rawHasError = (quoteStatusRaw &&
                    ['failed', 'rejected', 'eliminated'].includes(quoteStatusRaw)) ||
                    isReferred ||
                    partnerRejected ||
                    (policy === null || policy === void 0 ? void 0 : policy.insuranceStatus) === 'failed' ||
                    !!partnerErrorMsg;
                var applicationStatus = (_e = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.status) === null || _e === void 0 ? void 0 : _e.toLowerCase();
                var hasPaymentOptions = (quote === null || quote === void 0 ? void 0 : quote.paymentOptions) && (quote === null || quote === void 0 ? void 0 : quote.paymentOptions.length) > 0;
                var isPolicyActive = !!policy &&
                    (policy.insuranceStatus === 'active' ||
                        policy.insuranceStatus === 'bound');
                var isPolicyPending = !!policy &&
                    (policy.insuranceStatus === 'pending' ||
                        policy.insuranceStatus === 'queued');
                var isBinding = applicationStatus === 'binding' ||
                    isPolicyPending ||
                    (bindingQuoteKeys === null || bindingQuoteKeys === void 0 ? void 0 : bindingQuoteKeys.includes(quoteKey));
                var isPotentiallyLoading = !rawHasError &&
                    !isPolicyActive &&
                    !isBinding &&
                    (!hasPaymentOptions ||
                        ((_f = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _f === void 0 ? void 0 : _f.action) !==
                            UNIFIED_ACTION.SHOW_QUOTE_TO_FE);
                var isTimedOut = false;
                if (isPotentiallyLoading) {
                    var timestamp = (kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.updatedAt) || (quote === null || quote === void 0 ? void 0 : quote.updatedAt);
                    var startTime = void 0;
                    if (timestamp && !isNaN(new Date(timestamp).getTime())) {
                        startTime = new Date(timestamp).getTime();
                    }
                    else {
                        if (!loadingStartTimesRef.current[quoteKey]) {
                            loadingStartTimesRef.current[quoteKey] = Date.now();
                        }
                        startTime = loadingStartTimesRef.current[quoteKey];
                    }
                    if (Date.now() - startTime > FIFTEEN_MINUTES_MS) {
                        isTimedOut = true;
                    }
                }
                else {
                    delete loadingStartTimesRef.current[quoteKey];
                }
                var hasError = rawHasError || isTimedOut;
                var isDataLoading = !quote && !hasError;
                var isLoading = isPotentiallyLoading && !isTimedOut && !!quote;
                if (isTimedOut) {
                    quoteMappedStatus = 'FAILED';
                }
                else if (isLoading) {
                    quoteMappedStatus = 'LOADING';
                }
                var quoteAcronym = policyType.quoteAcronym;
                var currentSelection = selections[quoteKey];
                var isAcknowledged = acknowledgedQuotes.includes(quoteKey);
                var coverageKeys = Object.keys((quote === null || quote === void 0 ? void 0 : quote.coverageQuestions) || {});
                var isCoveragesQuestions = coverageKeys.length > 0;
                var isCoverageQuestionsAvailable = !isCoveragesQuestions ||
                    (((_h = (_g = quote === null || quote === void 0 ? void 0 : quote.coverageQuestions) === null || _g === void 0 ? void 0 : _g[coverageKeys[0]]) === null || _h === void 0 ? void 0 : _h.questions) || [])
                        .length > 0;
                var ackKeys = Object.keys((quote === null || quote === void 0 ? void 0 : quote.ackQuestions) || {});
                var isAckQuestions = ackKeys.length > 0;
                var isAckQuestionsAvailable = !isAckQuestions ||
                    (((_k = (_j = quote === null || quote === void 0 ? void 0 : quote.ackQuestions) === null || _j === void 0 ? void 0 : _j[ackKeys[0]]) === null || _k === void 0 ? void 0 : _k.questions) || []).length > 0;
                // --- Conflict Logic ---
                // Disable this quote if it's a GL/BOP and the other one is already binding/active
                var isDisabledByConflict = (policyType.code === 'GL' && hasActiveBOP) ||
                    (policyType.code === 'BOP' && hasActiveGL);
                var policyDisplayInfo = { number: '---', period: '---' };
                if (isPolicyActive && policy) {
                    var info = getPolicyInfo(policy, kaynaQuote);
                    if (info === null || info === void 0 ? void 0 : info.policyDetails) {
                        policyDisplayInfo = {
                            number: ((_l = info.policyDetails.find(function (d) { return d.name === 'Policy Number'; })) === null || _l === void 0 ? void 0 : _l.value) || '---',
                            period: ((_m = info.policyDetails.find(function (d) { return d.name === 'Active Period'; })) === null || _m === void 0 ? void 0 : _m.value) || '---'
                        };
                    }
                }
                var errorMessage = (((_q = (_p = (_o = quote === null || quote === void 0 ? void 0 : quote.failures) === null || _o === void 0 ? void 0 : _o[0]) === null || _p === void 0 ? void 0 : _p.reason) === null || _q === void 0 ? void 0 : _q.includes('API'))
                    ? 'Unable to quote this product online'
                    : (_s = (_r = quote === null || quote === void 0 ? void 0 : quote.failures) === null || _r === void 0 ? void 0 : _r[0]) === null || _s === void 0 ? void 0 : _s.reason) ||
                    partnerErrorMsg ||
                    (isTimedOut
                        ? 'Quote request timed out'
                        : 'Unable to quote this product online');
                var _z = getProduct(quoteKey), product = _z.product, application = _z.application;
                var isBuyNowDisabled = !currentSelection ||
                    isLoading ||
                    !product ||
                    !application ||
                    isDisabledByConflict;
                var isAcceptTermsDisabled = isLoading ||
                    !isAckQuestions ||
                    !product ||
                    !application ||
                    isDisabledByConflict;
                var isUpdateLimitDisabled = isLoading ||
                    !isCoveragesQuestions ||
                    !product ||
                    !application ||
                    isDisabledByConflict;
                var isQuestionsLoading = !isCoveragesQuestions || !isAckQuestions;
                var isProductInvalid = !product || !application;
                // Status text formatting
                var statusText = isPolicyActive
                    ? 'Active Policy'
                    : "QUOTE ".concat(((_t = quote === null || quote === void 0 ? void 0 : quote.status) === null || _t === void 0 ? void 0 : _t.toLowerCase()) === 'quoted' ||
                        ((_u = quote === null || quote === void 0 ? void 0 : quote.status) === null || _u === void 0 ? void 0 : _u.toLowerCase()) === 'rejected'
                        ? "ID: ".concat((quote === null || quote === void 0 ? void 0 : quote.externalQuoteReference) || '---')
                        : quoteMappedStatus);
                var statusColor = hasError
                    ? isReferred
                        ? 'text-amber-500'
                        : 'text-red-500'
                    : 'text-slate-400';
                // Payment Frequencies Processing
                var processedFrequencies = BASE_FREQUENCIES.map(function (freq) {
                    var _a;
                    var option = (_a = quote === null || quote === void 0 ? void 0 : quote.paymentOptions) === null || _a === void 0 ? void 0 : _a.find(function (opt) { return opt.installmentFrequency === freq.key; });
                    var isSelected = (currentSelection === null || currentSelection === void 0 ? void 0 : currentSelection.frequency) === freq.key;
                    var total = 0;
                    if (option) {
                        total =
                            Number(option.downpayment || 0) +
                                Number(option.installmentPayment || 0) *
                                    Number(option.installmentNumberOfTimes || 0);
                    }
                    return __assign(__assign({}, freq), { option: option, isSelected: isSelected, totalDisplay: formatDisplayMoney(total), totalFormatted: formatMoney(total), downpaymentDisplay: option
                            ? formatDisplayMoney(option.downpayment)
                            : '---', downpaymentFormatted: option
                            ? formatMoney(option.downpayment)
                            : '---', installmentFormatted: option
                            ? formatMoney(option.installmentPayment)
                            : '---', installmentCount: (option === null || option === void 0 ? void 0 : option.installmentNumberOfTimes) || 0 });
                }).filter(function (f) { return !!f.option; });
                // Limits processing
                var limits = {
                    occurrence: (quote === null || quote === void 0 ? void 0 : quote.occurrenceLimit)
                        ? formatDisplayMoney(quote.occurrenceLimit)
                        : '---',
                    aggregate: (quote === null || quote === void 0 ? void 0 : quote.aggregateLimit)
                        ? formatDisplayMoney(quote.aggregateLimit)
                        : '---',
                    deductible: (quote === null || quote === void 0 ? void 0 : quote.deductible)
                        ? formatDisplayMoney(quote.deductible)
                        : '---'
                };
                return {
                    quoteKey: quoteKey,
                    kaynaQuoteId: kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.id,
                    externalId: kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.externalId,
                    quoteExternalReference: quote === null || quote === void 0 ? void 0 : quote.externalQuoteReference,
                    externalQuote: quote,
                    policyType: policyType,
                    quoteAcronym: quoteAcronym,
                    description: POLICY_DESCRIPTIONS[quoteAcronym],
                    isDataLoading: isDataLoading,
                    hasError: hasError,
                    isReferred: isReferred,
                    isLoading: isLoading,
                    isBinding: isBinding,
                    isPolicyActive: isPolicyActive,
                    policyDisplayInfo: policyDisplayInfo,
                    errorMessage: errorMessage,
                    isDisabledByConflict: isDisabledByConflict,
                    isBuyNowDisabled: isBuyNowDisabled,
                    isAcceptTermsDisabled: isAcceptTermsDisabled,
                    isUpdateLimitDisabled: isUpdateLimitDisabled,
                    isQuestionsLoading: isQuestionsLoading,
                    isCoverageQuestionsAvailable: isCoverageQuestionsAvailable,
                    isAckQuestionsAvailable: isAckQuestionsAvailable,
                    isProductInvalid: isProductInvalid,
                    isAcknowledged: isAcknowledged,
                    statusText: statusText,
                    statusColor: statusColor,
                    alreadyManualSubmitted: (_v = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _v === void 0 ? void 0 : _v.manualSubmit,
                    isManualSubmitAvailable: Object.keys(((_x = (_w = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _w === void 0 ? void 0 : _w.application) === null || _x === void 0 ? void 0 : _x.answers) || {})
                        .length && !((_y = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _y === void 0 ? void 0 : _y.manualSubmit),
                    hideLimitsAndActions: !isPolicyActive &&
                        !isBinding &&
                        hasError &&
                        (isReferred || partnerRejected),
                    bindingText: (policy === null || policy === void 0 ? void 0 : policy.insuranceStatus) === 'queued'
                        ? 'Processing Policy...'
                        : 'Binding Quote...',
                    hasSelection: !!currentSelection,
                    frequencies: processedFrequencies,
                    limits: limits
                };
            });
            return { groupCode: groupCode, groupLabel: groupInfo.label, quotes: mappedQuotes };
        });
        return { processedGroups: processedGroups, processedNotStarted: processedNotStarted };
    }, [
        quoteKeys,
        allQuotes,
        quotes,
        selections,
        acknowledgedQuotes,
        bindingQuoteKeys,
        applicationFormData,
        Products,
        tick,
    ]), processedGroups = _q.processedGroups, processedNotStarted = _q.processedNotStarted;
    var askGlobalAck = useMemo(function () {
        var _a, _b, _c, _d;
        return (!!((_c = (_b = (_a = parentQuote === null || parentQuote === void 0 ? void 0 : parentQuote.product) === null || _a === void 0 ? void 0 : _a.Underwriter) === null || _b === void 0 ? void 0 : _b.carrierConfig) === null || _c === void 0 ? void 0 : _c.ackRequired) &&
            !((_d = parentQuote === null || parentQuote === void 0 ? void 0 : parentQuote.partnerData) === null || _d === void 0 ? void 0 : _d.acceptAck));
    }, [
        (_d = (_c = (_b = parentQuote === null || parentQuote === void 0 ? void 0 : parentQuote.product) === null || _b === void 0 ? void 0 : _b.Underwriter) === null || _c === void 0 ? void 0 : _c.carrierConfig) === null || _d === void 0 ? void 0 : _d.ackRequired,
        (_e = parentQuote === null || parentQuote === void 0 ? void 0 : parentQuote.partnerData) === null || _e === void 0 ? void 0 : _e.acceptAck,
    ]);
    var AcceptGlobalAck = function () {
        setGlobalAckLoading(true);
        ackQuote.mutateAsync({ quoteId: parentQuoteId });
    };
    var ManualReviewSubmit = function (id) {
        return submitForManualReview.mutateAsync({ quoteId: id });
    };
    // Timer effect to re-evaluate timeouts while active in background
    useEffect(function () {
        var hasLoadingQuote = processedGroups.some(function (group) {
            return group.quotes.some(function (q) { return q.isLoading || q.isDataLoading; });
        });
        if (!hasLoadingQuote)
            return;
        var interval = setInterval(function () {
            setTick(function (prev) { return prev + 1; });
        }, 10000);
        return function () { return clearInterval(interval); };
    }, [processedGroups]);
    // Effects
    useEffect(function () {
        setFooterOverride(false);
    }, []);
    useEffect(function () {
        quoteKeys.some(function (quoteKey) {
            var _a, _b, _c, _d, _e, _f;
            var kaynaQuote = quotes[quoteKey];
            var _g = getProduct(quoteKey), product = _g.product, application = _g.application;
            if ((((_a = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _a === void 0 ? void 0 : _a.action) ===
                UNIFIED_ACTION.SHOW_QUESTIONS_TO_FE ||
                Object.keys(((_c = (_b = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.partnerData) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.mapped) || {}).length) &&
                product &&
                application) {
                var answers = deepMerge(application.applicationDetails || {}, ((_d = kaynaQuote === null || kaynaQuote === void 0 ? void 0 : kaynaQuote.items) === null || _d === void 0 ? void 0 : _d.requiredParams) || {});
                setAnswers(answers);
                setProduct(product);
                setFormActive(true);
                setCurrentTab(CurrentTabEnum.POLICY);
                setSelectedQuote(kaynaQuote.id);
                setActiveDraftQuote(kaynaQuote.id);
                setCurrQuestionGroup(((_f = (_e = kaynaQuote.items) === null || _e === void 0 ? void 0 : _e.otherParams) === null || _f === void 0 ? void 0 : _f.currQuestionGroup) || 0);
                setUnifiedState({
                    parentQuoteId: parentQuoteId,
                    parentExternalId: parentQuote.externalId,
                    productBranding: productBranding,
                    activeQuoteId: kaynaQuote.id
                });
                return true;
            }
        });
    }, [getProduct, quotes, parentQuote.externalId]);
    useEffect(function () {
        if (!quotes)
            return;
        quoteKeys.forEach(function (quoteKey) {
            var _a, _b, _c;
            var quote = (_c = (_b = (_a = quotes[quoteKey]) === null || _a === void 0 ? void 0 : _a.partnerData) === null || _b === void 0 ? void 0 : _b.quotes) === null || _c === void 0 ? void 0 : _c[quoteKey];
            if (quote === null || quote === void 0 ? void 0 : quote.acknowledgementAnswers) {
                setAcknowledgedQuotes(function (prev) {
                    return !prev.includes(quoteKey) ? __spreadArray(__spreadArray([], prev, true), [quoteKey], false) : prev;
                });
            }
        });
    }, [quotes]);
    useEffect(function () {
        /*
         * Pre-Select last frequency option of each quote
         */
        if (processedGroups === null || processedGroups === void 0 ? void 0 : processedGroups.length) {
            processedGroups.forEach(function (group) {
                var _a;
                if ((_a = group === null || group === void 0 ? void 0 : group.quotes) === null || _a === void 0 ? void 0 : _a.length) {
                    group.quotes.forEach(function (q) {
                        if (!selections[q.quoteKey]) {
                            var freq = q.frequencies[q.frequencies.length - 1];
                            if (freq) {
                                handleFrequencySelect(q.quoteKey, q.quoteExternalReference, q.kaynaQuoteId, freq.option, freq.key, group.groupLabel);
                            }
                        }
                    });
                }
            });
        }
    }, [processedGroups]);
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        var savedQuote = (_a = unifiedState === null || unifiedState === void 0 ? void 0 : unifiedState.activeQuote) === null || _a === void 0 ? void 0 : _a.quote;
        if ((savedQuote === null || savedQuote === void 0 ? void 0 : savedQuote.quoteAcronym) && (savedQuote === null || savedQuote === void 0 ? void 0 : savedQuote.externalId)) {
            var code_1 = getCodeAndLabel(savedQuote.quoteKey).code;
            var currentQuote = (_c = (_b = processedGroups === null || processedGroups === void 0 ? void 0 : processedGroups.find(function (pg) { return pg.groupCode === code_1; })) === null || _b === void 0 ? void 0 : _b.quotes) === null || _c === void 0 ? void 0 : _c.find(function (q) { return q.externalId === savedQuote.externalId; });
            if (((_d = unifiedState === null || unifiedState === void 0 ? void 0 : unifiedState.activeQuote) === null || _d === void 0 ? void 0 : _d.state) === 'Ack-submit' &&
                !(currentQuote === null || currentQuote === void 0 ? void 0 : currentQuote.isAcknowledged) &&
                (currentQuote === null || currentQuote === void 0 ? void 0 : currentQuote.isLoading)) {
                setSelections(function (prev) {
                    var _a;
                    var _b;
                    return __assign(__assign({}, prev), (_a = {}, _a[savedQuote.quoteKey] = (_b = unifiedState.activeQuote) === null || _b === void 0 ? void 0 : _b.selectedPlan, _a));
                });
                setUnifiedState({
                    activeQuote: {
                        quote: savedQuote,
                        state: 'Buy-Pending'
                    }
                });
                setQuoteAckLoading(true);
            }
            else if (((_e = unifiedState === null || unifiedState === void 0 ? void 0 : unifiedState.activeQuote) === null || _e === void 0 ? void 0 : _e.state) === 'Buy-Pending' &&
                (currentQuote === null || currentQuote === void 0 ? void 0 : currentQuote.isAcknowledged) &&
                !(currentQuote === null || currentQuote === void 0 ? void 0 : currentQuote.isLoading)) {
                handleBuyNow(currentQuote);
                setQuoteAckLoading(false);
            }
        }
    }, [(_f = unifiedState === null || unifiedState === void 0 ? void 0 : unifiedState.activeQuote) === null || _f === void 0 ? void 0 : _f.state, quotes, processedGroups]);
    return {
        isInitialLoading: quoteKeys.length === 0,
        primaryColor: primaryColor,
        disabledBuyNowBgColor: disabledBuyNowBgColor,
        processedGroups: processedGroups,
        processedNotStarted: processedNotStarted,
        askGlobalAck: askGlobalAck,
        quoteAckLoading: quoteAckLoading,
        globalAckLoading: globalAckLoading,
        setGlobalAckLoading: setGlobalAckLoading,
        handleFrequencySelect: handleFrequencySelect,
        handleBuyNow: handleBuyNow,
        handleAck: handleAck,
        handleUpdateLimit: handleUpdateLimit,
        handleApplicationStart: handleApplicationStart,
        viewPolicyDetails: viewPolicyDetails,
        AcceptGlobalAck: AcceptGlobalAck,
        ManualReviewSubmit: ManualReviewSubmit
    };
};
